import { awaitsTeacher, isUnfinishedSource, needsKey, type ExerciseElement, type Plan } from "@edu/shared";
import { exerciseKindLabelLower } from "../../exercises/kind-label.ts";

/**
 * Задания заготовки с эталоном (`needsKey`) в порядке обхода слайдов: только
 * они доезжают до домашки, столбик ученику отвечать даёт, но в неё не едет.
 *
 * Порядок здесь не украшение: он же становится порядком ленты у ученика (3.1
 * docs/homework-design.md), потому что сервер компилирует задания в том же
 * обходе. Считать его вторым способом на клиенте значило бы завести второе
 * правило порядка, которое однажды разойдётся с первым.
 */
export interface AnswerableView {
  id: string;
  label: string;
  /**
   * У задания есть верный ответ, то есть оно доедет до ученика.
   *
   * ⚠️ Поле заведено 03.09.2026 и лечит враньё в числе на экране. Задание без
   * верного ответа сервер в домашку молча не берёт
   * (`apps/api/src/homework-exercises.ts`): человек отмечал восемь, создавалось
   * пять, и не было сказано ничего. Отказом это делать нельзя (редактор терял бы
   * первый же введённый символ), поэтому правда говорится ДО нажатия: такое
   * задание видно в списке и в счёт не идёт.
   *
   * Правило одно на клиент и сервер и живёт в `isUnfinishedSource`, а совпадение
   * с настоящей компиляцией охраняет `exercise-draft.test.ts`.
   */
  ready: boolean;
  /**
   * Почему задание не готово, словами для строки списка; `null` у готового.
   *
   * ⚠️ Причин две, и смешивать их нельзя (решение 22
   * `docs/scan-extraction-design.md`). У недособранного правильного ответа нет
   * вовсе, а у задания, которое ждёт преподавателя, ответ звучит в записи или
   * стоит на другой странице: такое задание урок не держит, а в домашку не
   * едет, потому что проверять его ответ некому, кроме преподавателя на уроке.
   * Подпись «без правильного ответа» увела бы человека вписывать ответ, который
   * он намеренно оставил себе.
   */
  reason: string | null;
}

export const NO_KEY_TEXT = "без правильного ответа";
export const TEACHER_CHECKS_TEXT = "ответ проверите вы на уроке";

/**
 * Подвал списка «Задания без правильного ответа в домашку не попадут. Поставить
 * его можно в заготовке» стоит, только когда такие задания есть. Задание,
 * которое ждёт преподавателя, его не зовёт: подвал увёл бы вписывать ответ,
 * который преподаватель оставил себе, а причина у такого задания стоит в его
 * строке.
 */
export function showsNoKeyNote(rows: readonly AnswerableView[]): boolean {
  return rows.some((row) => row.reason === NO_KEY_TEXT);
}

/** Причина у строки задания: в скобках, чтобы не слиться с подписью. */
export function reasonText(reason: string): string {
  return `(${reason})`;
}

/** Готово ли задание к домашке и если нет, почему. */
export function readiness(source: unknown): { ready: boolean; reason: string | null } {
  if (!isUnfinishedSource(source)) return { ready: true, reason: null };
  return { ready: false, reason: awaitsTeacher(source) ? TEACHER_CHECKS_TEXT : NO_KEY_TEXT };
}

/**
 * Как назвать задание в списке.
 *
 * ⚠️ Условие у пропусков лежит НЕ в `prompt`, а сегментами текста вперемешку с
 * полями: поля `prompt` у этого вида нет вовсе. Без первой ветки все пропуски
 * заготовки назывались бы одинаково, и выбрать из них было бы нельзя.
 *
 * ⚠️ Когда назвать нечем, печатается человеческое имя вида, а не `element.kind`.
 * `blanks` в списке у преподавателя это наше слово наружу: он ставил задание
 * кнопкой «Пропуски» и обязан узнать его под тем же именем.
 */
function labelOf(element: ExerciseElement): string {
  if (element.kind === "blanks") {
    // Пробелы схлопываются: вокруг пропуска они уже есть в тексте, и без этого
    // подпись получала бы двойной пробел на каждом поле. Заодно условие в два
    // абзаца становится одной строкой, а строка списка одна и есть
    const text = oneLine(element.segments.map((s) => ("t" in s ? s.t : " … ")).join(""));
    if (text !== "") return text;
  }
  // У перевода условия нет, есть предложения: первое отличает одно задание
  // перевода от соседнего лучше, чем имя вида
  if (element.kind === "translate" && element.items[0] !== undefined) {
    return `Перевод: ${oneLine(element.items[0].text)}`;
  }
  if ("prompt" in element && element.prompt.trim() !== "") return oneLine(element.prompt);
  return `задание «${exerciseKindLabelLower(element.kind)}»`;
}

function oneLine(text: string): string {
  return text.replace(/\s+/gu, " ").trim();
}

export function answerableOf(plan: Plan | null): AnswerableView[] {
  if (plan === null) return [];
  const out: AnswerableView[] = [];
  for (const slide of plan.slides) {
    for (const element of slide.exercises ?? []) {
      if (!needsKey(element.kind)) continue;
      out.push({
        id: element.id,
        label: labelOf(element),
        /*
          Источник берётся из самой заготовки: она уже приехала целиком
          (`usePlanSource`), и спрашивать сервер во второй раз не за чем.
          Отсутствие источника это не «не дособрано», а дефект заготовки, на
          который сервер отвечает отказом `invalid_exercise_source`: пометка
          увела бы человека доделывать то, чего нет.
        */
        ...readiness(plan.sources[element.id]),
      });
    }
  }
  return out;
}
